import React, { useCallback, useEffect, useState } from 'react';
import { BookCallContext } from './SiteUi';
import { submitContactForm } from '../services/contactService';
import { trackEvent } from '../services/analytics';
import TurnstileWidget from './TurnstileWidget';

const fieldClass = 'w-full rounded-xl border border-white/[0.08] bg-ink-light/70 px-4 py-3 text-sm text-cream placeholder:text-ash outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/20';
const labelClass = 'mb-1.5 block text-xs font-medium text-dust';
const errorClass = 'mt-1.5 text-xs text-red-300';

const emptyForm = { name: '', email: '', phone: '', message: '' };

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneDigits = (value) => value.replace(/\D/g, '');

export const validateBookCall = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Please enter your name.';

  const email = form.email.trim();
  const phone = form.phone.trim();
  if (!email && !phone) {
    errors.contact = 'Enter an email address or a phone number so we can reply.';
  }
  if (email && !emailPattern.test(email)) {
    errors.email = 'Please check the email address.';
  }
  if (phone && phoneDigits(phone).length < 8) {
    errors.phone = 'Please check the phone number.';
  }
  return errors;
};

export function BookCallProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState('website');

  const open = useCallback((options = {}) => {
    setSource(options.source || 'website');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <BookCallContext.Provider value={{ open }}>
      {children}
      {isOpen ? <BookCallModal source={source} onClose={close} /> : null}
    </BookCallContext.Provider>
  );
}

function Field({ id, label, required = false, error, children }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}{required ? ' *' : ''}
      </label>
      {children}
      {error ? <p className={errorClass}>{error}</p> : null}
    </div>
  );
}

export function BookCallForm({ source = 'website', onClose, onStepChange }) {
  const [step, setStepState] = useState('details'); // details -> confirm -> done
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileRequired, setTurnstileRequired] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [sentToEmail, setSentToEmail] = useState(false);

  const setStep = (next) => {
    setStepState(next);
    if (onStepChange) onStepChange(next);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name] || errors.contact) {
      setErrors((current) => ({ ...current, [name]: undefined, contact: undefined }));
    }
  };

  const handleReview = (event) => {
    event.preventDefault();
    const found = validateBookCall(form);
    setErrors(found);
    if (Object.keys(found).length === 0) setStep('confirm');
  };

  const handleSend = async () => {
    if (turnstileRequired && !turnstileToken) {
      setStatus({ status: 'error', message: 'Please complete the verification.' });
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const message = form.message.trim();
    const messageForCrm = [
      'Requested a call from the Book a call form.',
      message ? `Message: ${message}` : null,
    ].filter(Boolean).join('\n');

    const result = await submitContactForm({
      name,
      email,
      phone,
      message: messageForCrm,
      source,
      formName: 'CloudNext book a call',
      turnstileToken,
      customerSubject: 'Thanks for booking a call with CloudNext',
      customerBody: '<p>Hi there,</p><p>Thanks for booking a call. We have your details and will contact you as soon as we can.</p><p>Cheers,<br />Orfeo<br />CloudNext</p>',
      notificationSubject: `New call request from ${name}`,
      notificationBody: `<p><strong>Name:</strong> ${escapeHtml(name)}</p><p><strong>Email:</strong> ${escapeHtml(email) || 'Not given'}</p><p><strong>Phone:</strong> ${escapeHtml(phone) || 'Not given'}</p><p><strong>Source:</strong> ${escapeHtml(source)}</p><pre style="white-space:pre-wrap">${escapeHtml(message) || 'No message'}</pre>`,
    });

    setIsSubmitting(false);
    setTurnstileToken('');
    setTurnstileKey((key) => key + 1);

    if (result.status === 'success') {
      trackEvent('book_call_submit', { source });
      setSentToEmail(Boolean(email));
      setForm(emptyForm);
      setStep('done');
    } else {
      setStatus(result);
    }
  };

  return (
    <div>
      {step === 'details' ? (
        <form onSubmit={handleReview} noValidate className="grid gap-4">
          <p className="text-sm leading-6 text-dust">Add your name and one way for us to reply. We will contact you as soon as we can.</p>
          <Field id="book-name" label="Name" required error={errors.name}>
            <input id="book-name" name="name" value={form.name} onChange={handleChange} className={fieldClass} placeholder="Your name" autoComplete="name" />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="book-email" label="Email" error={errors.email}>
              <input id="book-email" name="email" type="email" value={form.email} onChange={handleChange} className={fieldClass} placeholder="you@business.com" autoComplete="email" />
            </Field>
            <Field id="book-phone" label="Phone" error={errors.phone}>
              <input id="book-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} className={fieldClass} placeholder="04xx xxx xxx" autoComplete="tel" />
            </Field>
          </div>
          {errors.contact ? <p className={errorClass}>{errors.contact}</p> : <p className="-mt-2 text-xs text-ash">Enter an email, a phone number, or both.</p>}

          <Field id="book-message" label="Message (optional)">
            <textarea id="book-message" name="message" rows={3} value={form.message} onChange={handleChange} className={fieldClass} placeholder="Anything we should know before we call?" />
          </Field>

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
            <button type="submit" className="inline-flex items-center justify-center rounded-2xl bg-gold px-6 py-3 text-sm font-semibold text-ink transition hover:bg-gold-light">
              Continue
            </button>
          </div>
        </form>
      ) : null}

      {step === 'confirm' ? (
        <div className="grid gap-5">
          <p className="text-sm leading-6 text-dust">Is this correct? We will use these details to contact you.</p>
          <dl className="grid gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-sm">
            <div><dt className="text-xs text-ash">Name</dt><dd className="text-cream">{form.name.trim()}</dd></div>
            <div><dt className="text-xs text-ash">Email</dt><dd className="text-cream">{form.email.trim() || 'Not given'}</dd></div>
            <div><dt className="text-xs text-ash">Phone</dt><dd className="text-cream">{form.phone.trim() || 'Not given'}</dd></div>
            <div><dt className="text-xs text-ash">Message</dt><dd className="whitespace-pre-wrap text-cream">{form.message.trim() || 'None'}</dd></div>
          </dl>

          {status ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{status.message}</div>
          ) : null}

          <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} onConfigured={setTurnstileRequired} />

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <button type="button" onClick={() => setStep('details')} disabled={isSubmitting} className="rounded-2xl border border-white/[0.1] px-6 py-3 text-sm text-cream hover:border-gold/40 disabled:opacity-50">
              Edit details
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={isSubmitting || (turnstileRequired && !turnstileToken)}
              className="inline-flex items-center justify-center rounded-2xl bg-gold px-6 py-3 text-sm font-semibold text-ink transition hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Yes, send my details'}
            </button>
          </div>
        </div>
      ) : null}

      {step === 'done' ? (
        <div className="grid gap-5">
          <p className="text-sm leading-6 text-dust">
            We have your details and will contact you as soon as we can.{sentToEmail ? ' A confirmation email is on its way.' : ''}
          </p>
          {onClose ? (
            <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-2xl bg-gold px-6 py-3 text-sm font-semibold text-ink transition hover:bg-gold-light">
              Close
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function BookCallModal({ source, onClose }) {
  const [step, setStep] = useState('details');

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const titleId = 'book-call-title';
  const title = step === 'done' ? 'Thanks, your request is in' : step === 'confirm' ? 'Check your details' : 'Book a call';

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/80 p-4 backdrop-blur-sm sm:items-center" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/[0.08] bg-ink-light p-6 shadow-2xl md:p-8">
        <div className="flex items-start justify-between gap-4">
          <h2 id={titleId} className="font-display text-2xl text-cream">{title}</h2>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/[0.08] px-3 py-1.5 text-sm text-dust hover:text-cream">
            Close
          </button>
        </div>
        <div className="mt-6">
          <BookCallForm source={source} onClose={onClose} onStepChange={setStep} />
        </div>
      </div>
    </div>
  );
}
